﻿import {
  NotificationsApi,
  RCSAvailabilityStatus,
  ScheduleLocalNotification,
  ScheduleNotificationOptions,
  ScheduleRCSInput,
  ScheduleRCSResult,
  RequestRCSOptInInput,
  RequestRCSOptInResult,
  SubmitMessageInput,
  SubmitMessageResult,
} from './NotificationsApi'
import {
  createMockDelay,
  isWebPlatform,
  MOCK_DELAYS,
} from '../rundot-game-api/systems/core'
import { RundotGameAPI } from '../rundot-game-api/types'
import { mockLog } from '../MockDebug'

const TAG = 'Mock Notifications'

export class MockNotificationsApi implements NotificationsApi {
  private readonly rundotGameApi: RundotGameAPI

  constructor(rundotGameApi: RundotGameAPI) {
    this.rundotGameApi = rundotGameApi
  }

  async cancelNotification(notificationId: string): Promise<boolean> {
    mockLog(TAG, `cancelNotification id=${notificationId}`)
    const rundotGameApi = this.rundotGameApi

    // Check if notifications are supported on this platform
    if (isWebPlatform()) {
      return true
    }

    // Simulate a delay
    await createMockDelay(MOCK_DELAYS.short)

    // Remove from mock storage
    if (
      rundotGameApi._mock.scheduledNotifications &&
      rundotGameApi._mock.scheduledNotifications[notificationId]
    ) {
      delete rundotGameApi._mock.scheduledNotifications[notificationId]
      return true
    }

    return false
  }

  async getAllScheduledLocalNotifications(): Promise<
    ScheduleLocalNotification[]
  > {
    // Check if notifications are supported on this platform
    if (isWebPlatform()) {
      // Return direct array (not wrapped in success object)
      return []
    }

    // Simulate a delay
    await createMockDelay(MOCK_DELAYS.short)

    const rundotGameApi = this.rundotGameApi

    // Return direct array of notifications (not wrapped in success object)
    const notifications = rundotGameApi._mock.scheduledNotifications || {}
    return Object.values(notifications)
  }

  async isLocalNotificationsEnabled(): Promise<boolean> {
    // Check if notifications are supported on this platform
    if (isWebPlatform()) {
      // Return direct boolean (not wrapped in success object)
      return false
    }

    // Simulate a delay
    await createMockDelay(MOCK_DELAYS.short)

    const rundotGameApi = this.rundotGameApi
    // Return direct boolean (not wrapped in success object)
    const isEnabled = rundotGameApi._mock.notificationsEnabled !== false
    return isEnabled
  }

  async submitMessageAsync(input: SubmitMessageInput): Promise<SubmitMessageResult> {
    mockLog(TAG, `submitMessageAsync channels=${input.channels.join(',')}`)
    await createMockDelay(MOCK_DELAYS.short)

    const messageId = `mock-message-${Date.now()}`
    const results: SubmitMessageResult['results'] = []

    if (input.channels.includes('local')) {
      if (isWebPlatform()) {
        results.push({
          channel: 'local',
          status: 'skipped',
          reason: 'unsupported_platform',
        })
      } else {
        const id = input.notificationId || `mock-notification-${Date.now()}`
        results.push({ channel: 'local', status: 'scheduled', id })
      }
    }

    const wantsServer = input.channels.includes('rcs')
    if (wantsServer && !isWebPlatform()) {
      results.push({ channel: 'inbox', status: 'scheduled', id: messageId })
      results.push({
        channel: 'rcs',
        status: 'scheduled',
        id: `mock-rcs-${Date.now()}`,
      })
    } else if (wantsServer && isWebPlatform()) {
      results.push({
        channel: 'rcs',
        status: 'skipped',
        reason: 'unsupported_platform',
      })
    }

    return { messageId, results }
  }

  async scheduleAsync(
    title: string,
    body: string,
    seconds: number,
    notificationId?: string,
    options?: ScheduleNotificationOptions
  ): Promise<string | null> {
    mockLog(TAG, `scheduleAsync title="${title}" seconds=${seconds}`)
    const { priority = 50, groupId, payload } = options || {}
    
    // Check if notifications are supported on this platform
    if (isWebPlatform()) {
      // Return direct mock ID (not wrapped in success object)
      const mockId = `mock-web-notification-${Date.now()}`
      return mockId
    }

    const rundotGameApi = this.rundotGameApi
    // Initialize pendingRequests if it doesn't exist
    if (!rundotGameApi._mock.pendingRequests) {
      rundotGameApi._mock.pendingRequests = {}
    }

    const requestId = Date.now().toString()

    return new Promise((resolve) => {
      rundotGameApi._mock.pendingRequests[requestId] = { resolve }

      // Generate a mock notification ID
      const id = notificationId || `mock-notification-${Date.now()}`

      // Store this notification in mock storage for later retrieval
      if (!rundotGameApi._mock.scheduledNotifications) {
        rundotGameApi._mock.scheduledNotifications = {}
      }

      rundotGameApi._mock.scheduledNotifications[id] = {
        id,
        title,
        body,
        payload,
        seconds,
      }

      // Simulate a delay using standard timing
      setTimeout(() => {
        // Return direct notification ID (not wrapped in success object)
        resolve(id)
      }, MOCK_DELAYS.short)
    })
  }

  async setLocalNotificationsEnabled(enabled: boolean): Promise<boolean> {
    const rundotGameApi = this.rundotGameApi

    // Check if notifications are supported on this platform
    if (isWebPlatform()) {
      // Return void for successful setting (like the real API)
      return true
    }

    // Simulate a delay
    await createMockDelay(MOCK_DELAYS.short)

    // Update mock storage
    rundotGameApi._mock.notificationsEnabled = enabled
    return enabled
  }

  async getRCSAvailableAsync(): Promise<RCSAvailabilityStatus> {
    mockLog(TAG, `getRCSAvailableAsync()`)
    await createMockDelay(MOCK_DELAYS.short)
    return { available: true }
  }

  async scheduleRCSAsync(input: ScheduleRCSInput): Promise<ScheduleRCSResult> {
    mockLog(
      TAG,
      `scheduleRCSAsync title="${input.title}" triggerAt=${input.triggerAt ?? ''} delaySeconds=${input.delaySeconds ?? ''}`,
    )
    await createMockDelay(MOCK_DELAYS.short)
    return {
      scheduleId: `mock-rcs-${Date.now()}`,
      status: 'pending',
    }
  }

  async requestRCSOptInAsync(
    input?: RequestRCSOptInInput,
  ): Promise<RequestRCSOptInResult> {
    mockLog(TAG, `requestRCSOptInAsync rewardCopy="${input?.rewardCopy ?? ''}"`)
    await createMockDelay(MOCK_DELAYS.short)
    return { status: 'subscribed', newlySubscribed: true }
  }
}
